import { redirect, type ParsedLocation } from "@tanstack/react-router";
import { AUTH_KEY } from "@/components/constants";

/**
 * Authentication guard function that can be used in route beforeLoad
 * Checks if user has a valid token in localStorage
 * Redirects to login page if not authenticated
 */
export const requireAuth = async ({
  location,
}: {
  location: ParsedLocation;
}) => {
  // Check authentication by directly accessing localStorage
  const storedData = localStorage.getItem(AUTH_KEY);

  if (!storedData) {
    throw redirect({
      to: "/",
      search: {
        redirect: location.href,
      },
    });
  }

  try {
    const userData = JSON.parse(storedData);
    const isAuthenticated = !!userData.accessToken;

    if (!isAuthenticated) {
      throw redirect({
        to: "/",
        search: {
          redirect: location.href,
        },
      });
    }

    // Return user data if needed
    return userData;
  } catch {
    // If parsing fails, clear invalid data and redirect
    localStorage.removeItem(AUTH_KEY);
    throw redirect({
      to: "/",
      search: {
        redirect: location.href,
      },
    });
  }
};

/**
 * Check if user is authenticated (for use in components)
 * Returns boolean indicating authentication status
 */
export const isAuthenticated = (): boolean => {
  try {
    const storedData = localStorage.getItem(AUTH_KEY);
    if (!storedData) return false;

    const userData = JSON.parse(storedData);
    return !!userData.accessToken;
  } catch {
    return false;
  }
};

/**
 * Get user data from localStorage (for use in components)
 * Returns user data or null if not authenticated
 */
export const getUserData = () => {
  try {
    const storedData = localStorage.getItem(AUTH_KEY);
    if (!storedData) return null;

    const userData = JSON.parse(storedData);
    return userData.accessToken ? userData : null;
  } catch {
    return null;
  }
};
